import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Calendar, 
  Award, 
  Users, 
  Lightbulb,
  Target,
  Heart,
  Rocket
} from 'lucide-react';

const About = () => {
  const timelineEvents = [
    {
      year: "2005",
      title: "The Beginning",
      description: "Started as a one-person web design shop, building simple HTML websites for local businesses.",
      icon: <Rocket className="h-4 w-4" />
    },
    {
      year: "2008",
      title: "WordPress Revolution", 
      description: "Embraced WordPress as our primary platform, empowering clients to manage their own content.",
      icon: <Lightbulb className="h-4 w-4" />
    },
    {
      year: "2012",
      title: "Mobile-First Approach",
      description: "Pioneered responsive design practices as mobile usage exploded across all industries.",
      icon: <Target className="h-4 w-4" />
    },
    {
      year: "2015",
      title: "Team Expansion",
      description: "Grew to a team of 5 specialists, allowing us to take on larger, more complex projects.",
      icon: <Users className="h-4 w-4" />
    },
    {
      year: "2018",
      title: "Client Empowerment Focus",
      description: "Launched our signature client training program, ensuring complete website independence.",
      icon: <Award className="h-4 w-4" />
    },
    {
      year: "2021",
      title: "Modern Stack Adoption",
      description: "Integrated cutting-edge technologies while maintaining our commitment to simplicity and control.",
      icon: <Lightbulb className="h-4 w-4" />
    },
    {
      year: "2024",
      title: "20 Years Strong",
      description: "Celebrating two decades of building the web and empowering businesses with 500+ successful projects.",
      icon: <Heart className="h-4 w-4" />
    }
  ];

  const values = [
    {
      title: "Transparency",
      description: "No hidden fees, no confusing jargon. We believe in honest communication and clear pricing.",
      icon: <Award className="h-6 w-6 text-primary" />
    },
    {
      title: "Empowerment", 
      description: "We don't just build websites; we teach you to manage them. Your success is our success.",
      icon: <Users className="h-6 w-6 text-primary" />
    },
    {
      title: "Quality",
      description: "20 years of experience means we know what works. Every website is built to last and perform.",
      icon: <Target className="h-6 w-6 text-primary" />
    },
    {
      title: "Innovation",
      description: "We stay current with technology trends while maintaining focus on what truly matters: results.",
      icon: <Lightbulb className="h-6 w-6 text-primary" />
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-primary/10 text-primary border-primary/20 font-body">
                Our Story
              </Badge>
              <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground">
                20 Years of
                <span className="text-gradient"> Digital Evolution</span>
              </h1>
              <p className="font-body text-lg text-muted-foreground">
                From the early days of table-based layouts to today's modern frameworks, 
                we've been there through every major shift in web development. Our journey 
                is one of constant learning, adaptation, and an unwavering commitment to 
                putting our clients in control.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                >
                  <Link to="/booking">
                    Work With Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg" 
                  className="font-heading font-semibold"
                >
                  <Link to="/case-studies">View Our Work</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/team_working_1.jpg"
                alt="Our team at work"
                className="rounded-lg shadow-elegant w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-accent/10 text-accent border-accent/20 font-body mb-4">
              Our Journey
            </Badge>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-6">
              Two Decades of
              <span className="text-gradient"> Innovation & Growth</span>
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
              Every milestone in our journey has been driven by one goal: creating better 
              websites and empowering our clients to succeed online.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timelineEvents.map((event, index) => (
                <div key={index} className="timeline-item">
                  <Card className="hover-lift shadow-card border-0 bg-white ml-8">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                            {event.icon}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge className="bg-primary/10 text-primary border-primary/20 font-body">
                              {event.year}
                            </Badge>
                            <h3 className="font-heading text-xl font-bold text-foreground">
                              {event.title}
                            </h3>
                          </div>
                          <p className="font-body text-muted-foreground">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              These principles have guided us for 20 years and continue to shape every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="bg-white/10 border-white/20 hover-lift">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    {value.icon}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-primary-foreground mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-sm text-primary-foreground/80">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              The Numbers Tell Our Story
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Two decades of consistent growth and client satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-primary">500+</div>
              <div className="font-body text-muted-foreground">Websites Built</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">20</div>
              <div className="font-body text-muted-foreground">Years in Business</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-primary">98%</div>
              <div className="font-body text-muted-foreground">Client Retention</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-4xl md:text-5xl font-bold text-accent">24/7</div>
              <div className="font-body text-muted-foreground">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Meet the Team Behind
              <span className="text-gradient"> Your Success</span>
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Our experienced team combines decades of web development expertise with a 
              passion for empowering businesses to succeed online.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <Card className="hover-lift shadow-card">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">Development Team</h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Expert developers with 20+ years combined experience
                  </p>
                </CardContent>
              </Card>

              <Card className="hover-lift shadow-card">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="h-10 w-10 text-accent" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">Design Team</h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Creative designers focused on user experience and conversion
                  </p>
                </CardContent>
              </Card>

              <Card className="hover-lift shadow-card">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">Support Team</h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Dedicated support specialists ensuring your success
                  </p>
                </CardContent>
              </Card>
            </div>
            <Button 
              asChild 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
            >
              <Link to="/booking">
                Start Your Project Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;